import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type { AuthSession } from '@/lib/authToken'

export interface AuthTokens {
  accessToken: string
  idToken: string
  refreshToken?: string
  expiresIn: number
  tokenType: string
}

export interface NewPasswordChallenge {
  challenge: 'NEW_PASSWORD_REQUIRED'
  session: string
  email: string
}

export type SignInResult = AuthTokens | NewPasswordChallenge

export interface SsoProviderSummary {
  name: string
  displayName: string
}

/** The `code` the backend put in an error body (USE_SSO, NO_ACCOUNT, REAUTH_REQUIRED, …). */
export function errorCode(err: unknown): string | undefined {
  const code = (err as { code?: unknown } | null)?.code
  return typeof code === 'string' ? code : undefined
}

export function isNewPasswordChallenge(r: SignInResult): r is NewPasswordChallenge {
  return (r as NewPasswordChallenge).challenge === 'NEW_PASSWORD_REQUIRED'
}

/**
 * Two transports, because this client mixes two kinds of route.
 *
 * Most routes here check a CREDENTIAL — a password, a reset code, a
 * new-password challenge — and a 401 from one means that credential was wrong.
 * Those go through bare `fetch`: renewing the session and replaying a wrong
 * password would spend a refresh-token exchange to get the same answer, and
 * `/auth/refresh` itself must never renew against itself.
 *
 * `/auth/me` is the exception. It is an ordinary authenticated read and the
 * FIRST request every page load makes, so a 401 there almost always means the
 * access token lapsed while the tab was closed. Sending it through bare `fetch`
 * turned that into a sign-out: the stale token 401'd, `handleUnauthorized`
 * cleared the session, and the still-valid refresh token was never tried. It
 * goes through `authFetch`, which renews once and replays.
 */
async function send<T>(
  transport: (input: string, init?: RequestInit) => Promise<Response>,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const res = await transport(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText)
    let message = text
    let code: string | undefined
    try {
      const body = JSON.parse(text) as { message?: string; code?: string }
      message = body.message ?? text
      code = body.code
    } catch {
      // not JSON; keep the raw text
    }
    // REAUTH_REQUIRED is a 401 that asks for a fresh SSO login, not a dead session.
    if (code !== 'REAUTH_REQUIRED') handleUnauthorized(res.status)
    throw Object.assign(new Error(message || `Auth request failed (${res.status})`), {
      status: res.status,
      code,
    })
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

/** Credential-check routes: a 401 is a wrong credential, never a lapsed token. */
const request = <T>(path: string, init?: RequestInit) =>
  send<T>((input, i) => fetch(input, i), path, init)

/** Authenticated reads: a 401 is usually a lapsed token, so renew and replay. */
const sessionRequest = <T>(path: string, init?: RequestInit) =>
  send<T>((input, i) => authFetch(input, i), path, init)

export const authApi = {
  signin: (email: string, password: string): Promise<SignInResult> =>
    request<SignInResult>('/auth/signin', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  // Complete a first-login NEW_PASSWORD_REQUIRED challenge.
  completeNewPassword: (
    email: string,
    newPassword: string,
    session: string,
  ): Promise<AuthTokens> =>
    request<AuthTokens>('/auth/complete-new-password', {
      method: 'POST',
      body: JSON.stringify({ email, newPassword, session }),
    }),

  refresh: (refreshToken: string): Promise<AuthTokens> =>
    request<AuthTokens>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    }),

  // Exchange the signed-in user's own password for a single-use signing token
  // (21 CFR Part 11). No email is sent: the server checks the password against
  // the authenticated principal, so this cannot be used to test anyone else's.
  // A wrong password throws a 401 — surface it as "password incorrect", not as
  // a session problem.
  createSigningToken: (password: string): Promise<{ token: string; expiresIn: number }> =>
    request<{ token: string; expiresIn: number }>('/auth/signing-token', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  // Self-service password reset — step 1. Emails a verification code. The backend
  // returns a generic acknowledgement (never reveals whether the account exists).
  forgotPassword: (email: string): Promise<{ message: string }> =>
    request<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  // Self-service password reset — step 2. Confirms the emailed code and sets the
  // new password. A bad/expired code or weak password come back as a thrown 400.
  confirmForgotPassword: (
    email: string,
    code: string,
    newPassword: string,
  ): Promise<{ message: string }> =>
    request<{ message: string }>('/auth/confirm-forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email, code, newPassword }),
    }),

  ssoConfig: (): Promise<{ enabled: boolean; providers: SsoProviderSummary[]; logoutUrl?: string }> =>
    request<{ enabled: boolean; providers: SsoProviderSummary[]; logoutUrl?: string }>('/auth/sso/config'),

  // `credentials: 'include'` carries the httpOnly `sso_tx` cookie set by /auth/sso/start,
  // which holds the PKCE verifier and the state this code is checked against.
  ssoComplete: (code: string, state: string): Promise<AuthTokens & { next?: string }> =>
    request<AuthTokens & { next?: string }>('/auth/sso/complete', {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({ code, state }),
    }),

  // SSO-only users sign with this password. Needs an SSO login from the last
  // 10 minutes, otherwise 401 REAUTH_REQUIRED.
  setSigningPassword: (password: string): Promise<void> =>
    request<void>('/users/me/signing-password', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  // The current principal with roles + effective permissions (resolved server-side).
  // Through `sessionRequest`, not `request` — see `send` above for why this one
  // route renews.
  me: (): Promise<AuthSession> => sessionRequest<AuthSession>('/auth/me'),
}
